import { useState } from 'react';
import type { BrushOptions } from '@design-ai/gpu-core';
import { DEFAULT_MATTE_SETTINGS } from '@design-ai/pipeline';
import type { Engine } from '../engine.js';
import type { Tool } from './Viewport.js';
import { Section, Select, Slider, Toggle } from './controls.js';

export interface MattePanelProps {
  engine: Engine;
  ready: boolean;
  busy: boolean;
  tool: Tool;
  brush: BrushOptions;
  showAlpha: boolean;
  onToolChange: (tool: Tool) => void;
  onBrushChange: (brush: BrushOptions) => void;
  onShowAlphaChange: (value: boolean) => void;
  onRunMatting: () => void;
  onRefine: () => void;
}

export function MattePanel({
  engine,
  ready,
  busy,
  tool,
  brush,
  showAlpha,
  onToolChange,
  onBrushChange,
  onShowAlphaChange,
  onRunMatting,
  onRefine,
}: MattePanelProps) {
  const [, forceRender] = useState(0);
  const settings = engine.workbench.matteSettings;

  // Every knob below is GPU post-processing over the cached network output, so
  // dragging a slider re-runs in milliseconds and never re-runs the model.
  const update = <K extends keyof typeof settings>(key: K, value: (typeof settings)[K]): void => {
    settings[key] = value;
    forceRender((n) => n + 1);
    onRefine();
  };

  const hasMatte = engine.workbench.state.hasMatte;

  return (
    <Section title="Cut out" subtitle="Run once, then tune - the model is not re-run.">
      <button type="button" className="button button--primary" disabled={!ready || busy} onClick={onRunMatting}>
        {busy ? 'Working...' : hasMatte ? 'Re-run background removal' : 'Remove background'}
      </button>

      {hasMatte ? (
        <>
          <Slider
            label="Edge refinement"
            value={settings.guidedRadius}
            min={0}
            max={32}
            step={1}
            onChange={(value) => update('guidedRadius', value)}
            format={(value) => (value === 0 ? 'off' : `${value}px`)}
            hint="Guided filter radius. Snaps alpha onto the artwork's own edges."
          />
          <Slider
            label="Edge tightness"
            value={Math.log10(settings.guidedEps)}
            min={-6}
            max={-2}
            step={0.1}
            onChange={(value) => update('guidedEps', 10 ** value)}
            format={(value) => (10 ** value).toExponential(1)}
            hint="Lower hugs every gradient; raise it if fabric texture leaks into the matte."
          />
          <Slider
            label="Choke / spread"
            value={settings.choke}
            min={-6}
            max={6}
            step={1}
            onChange={(value) => update('choke', value)}
            format={(value) => (value === 0 ? 'none' : `${value > 0 ? '+' : ''}${value}px`)}
            hint="Negative shrinks the matte - the usual fix for a pale halo."
          />
          <Slider
            label="Threshold low"
            value={settings.low}
            min={0}
            max={1}
            step={0.01}
            onChange={(value) => update('low', Math.min(value, settings.high - 0.01))}
          />
          <Slider
            label="Threshold high"
            value={settings.high}
            min={0}
            max={1}
            step={0.01}
            onChange={(value) => update('high', Math.max(value, settings.low + 0.01))}
          />
          <Slider
            label="Remove specks"
            value={settings.minIslandFraction * 10000}
            min={0}
            max={50}
            step={1}
            onChange={(value) => update('minIslandFraction', value / 10000)}
            format={(value) => (value === 0 ? 'off' : `${(value / 100).toFixed(2)}% of frame`)}
            hint="Deletes disconnected fragments: collar slivers, watermark corners, JPEG speckle."
          />
          <Slider
            label="Decontaminate"
            value={settings.despill}
            min={0}
            max={1}
            step={0.05}
            onChange={(value) => update('despill', value)}
            hint={`Removes the garment colour bleeding into edge pixels (currently ${formatColor(
              engine.workbench.effectiveBackground,
            )}).`}
          />
          <Toggle
            label="Show alpha channel"
            checked={showAlpha}
            onChange={onShowAlphaChange}
            hint="The fastest way to spot a halo or a hole."
          />

          <div className="tools">
            <Select<Tool>
              label="Tool"
              value={tool}
              options={[
                { value: 'pan', label: 'Pan / zoom' },
                { value: 'erase', label: 'Erase (force transparent)' },
                { value: 'restore', label: 'Restore (force opaque)' },
              ]}
              onChange={onToolChange}
              hint="Brush strokes live in their own layer, so sliders keep working afterwards."
            />
            <Slider
              label="Brush size"
              value={brush.radius}
              min={2}
              max={200}
              step={1}
              onChange={(value) => onBrushChange({ ...brush, radius: value })}
              format={(value) => `${value}px`}
            />
            <Slider
              label="Brush hardness"
              value={brush.hardness ?? 0.6}
              min={0}
              max={1}
              step={0.05}
              onChange={(value) => onBrushChange({ ...brush, hardness: value })}
            />
            <button
              type="button"
              className="button"
              onClick={() => {
                engine.workbench.clearPaint();
                onRefine();
              }}
            >
              Clear manual edits
            </button>
          </div>

          <button
            type="button"
            className="button"
            onClick={() => {
              Object.assign(settings, DEFAULT_MATTE_SETTINGS);
              forceRender((n) => n + 1);
              onRefine();
            }}
          >
            Reset settings
          </button>
        </>
      ) : null}
    </Section>
  );
}

function formatColor([r, g, b]: readonly [number, number, number]): string {
  const hex = (value: number): string =>
    Math.round(Math.min(1, Math.max(0, value)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
